import type { AddressResult } from '@chain-zen/ui'
import { AddressSearchInput, Button, Tooltip, X } from '@chain-zen/ui'
import { useTheme } from 'next-themes'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import type { IAddress, IStopWarehouse } from '../../../../../types/shipment'

interface IProps {
  currentWarehouse: IStopWarehouse | null
  locationID: string
  onAddressUpdate: (warehouse: IStopWarehouse | null) => void
  onCancel: () => void
}

function getAddressLabelFromAddressString(s: string) {
  return s.split(',')[0].trim()
}

export const UpdateStopWarehouse = ({ locationID, onCancel, onAddressUpdate, currentWarehouse }: IProps) => {
  const { theme } = useTheme()
  const stopWarehouseAddress = currentWarehouse?.address

  const methods = useForm({
    defaultValues: {
      warehouse: stopWarehouseAddress?.addressLabel || ''
    }
  })

  const handleUpdateAddress = (address: IAddress, locationID: string) => {
    // Create updated warehouse object with new address and location
    const updatedWarehouse: IStopWarehouse = {
      address,
      category: currentWarehouse?.category || null,
      geofence: currentWarehouse?.geofence ?? null,
      locationID,
      name: address.addressLabel || currentWarehouse?.name || null,
      placeID: currentWarehouse?.placeID || null,
      rating: currentWarehouse?.rating || null,
      reviews: currentWarehouse?.reviews || null,
      thumbnail: currentWarehouse?.thumbnail || null,
      warehouseHours: currentWarehouse?.warehouseHours || null
    }

    onAddressUpdate(updatedWarehouse)
  }

  const handlePlaceSelect = (event: React.SyntheticEvent<HTMLInputElement> | CustomEvent<AddressResult>) => {
    if (!('detail' in event)) return

    const {
      addressLabel,
      city,
      country,
      postalCode,
      countryCode,
      state,
      stateCode,
      street,
      placeID,
      streetNumber,
      latitude,
      longitude,
      type
    } = event.detail

    const title = addressLabel ? getAddressLabelFromAddressString(addressLabel) : ''

    const address = {
      addressLabel,
      city: city || null,
      coordinates: { latitude, longitude },
      country: country || null,
      countryCode: countryCode || null,
      county: null,
      placeID: placeID || null,
      postalCode: postalCode || null,
      postalCodeSuffix: null,
      state: state || null,
      stateCode: stateCode || null,
      street: street || null,
      streetNumber: streetNumber || null,
      title: title,
      type
    }

    handleUpdateAddress(address, locationID)
  }

  return (
    <div className="flex flex-col gap-4 w-full">
      <FormProvider {...methods}>
        <form className="space-y-4">
          <div className="relative w-full flex items-center gap-2">
            <Controller
              name="warehouse"
              control={methods.control}
              render={({ field }) => (
                <div className="relative w-full">
                  <AddressSearchInput
                    {...field}
                    placeholder="Enter warehouse name or address"
                    name={'warehouse'}
                    onSelect={handlePlaceSelect}
                    currentTheme={theme as 'light' | 'dark'}
                  />
                </div>
              )}
            />

            <Tooltip content="Cancel">
              <Button variant="ghost" size="sm" onClick={onCancel} aria-label="Cancel">
                <X className="h-4 w-4" />
              </Button>
            </Tooltip>
          </div>
        </form>
      </FormProvider>
    </div>
  )
}
